import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, InlineError, Input, PhoneInput, Skeleton, Textarea } from '../../../components/ui'
import {
  selectSubtotal,
  selectTotal,
  selectTotalDuration,
  setCoupon,
  setIdempotencyKey,
} from '../../../features/booking/selectionSlice'
import { useCreateBookingMutation, useValidateCouponMutation } from '../../../services/bookingApi'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useTenant } from '../../../tenant/useTenant'
import { useToast } from '../../../hooks/useToast'
import { cn } from '../../../utils/cn'
import { addMinutesToIso, formatLongDate, formatSlotTime, slotDate } from '../../../utils/datetime'
import { formatDuration, formatPrice } from '../../../utils/format'
import { isPlausibleMobile, toE164, toNationalDigits } from '../../../utils/phone'
import { useSession } from '../../auth/useSession'
import { newIdempotencyKey } from '../idempotency'
import { rememberBooking } from '../lastBooking'
import { useWizardAction } from '../wizardContext'

interface FieldErrors {
  name?: string
  phone?: string
  email?: string
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

function validate(name: string, phone: string, email: string): FieldErrors {
  const errors: FieldErrors = {}
  if (name.trim().length < 2) errors.name = 'Enter your name so we know who to expect.'
  if (!phone) errors.phone = 'Enter the phone number we should text the confirmation to.'
  else if (!isPlausibleMobile(phone)) errors.phone = 'That number does not look right — we need all 10 digits.'
  if (email.trim() && !EMAIL_RE.test(email.trim())) errors.email = 'That email address does not look right. Check it for a typo.'
  return errors
}

/**
 * The last step for someone who is not signed in. Three doors, not two: create
 * an account, sign in, or finish as a guest — the old quick-booking path, which
 * the backend still serves by matching or creating a customer from the phone
 * number. Signing up asks for the number first and the backend answers "that
 * number already has an account, please sign in" when it recognises it, so a
 * returning customer who picks the wrong door is told, not silently handed a
 * duplicate. Whether the guest door is there at all is the vendor's call:
 * `flags.guest_checkout`.
 */
function FinishOptions({
  slug,
  checking,
  guestAllowed,
  onGuest,
}: {
  slug: string
  checking: boolean
  guestAllowed: boolean
  onGuest: () => void
}) {
  const next = encodeURIComponent(`/${slug}/book/details`)

  if (checking) {
    return (
      <div className="container-page flex flex-col gap-4 py-12 md:py-section-gap-md">
        <Skeleton shape="rect" className="h-8 w-64" />
        <Skeleton shape="rect" className="h-40 w-full max-w-xl" />
      </div>
    )
  }

  return (
    <div className="container-page py-12 md:py-section-gap-md">
      <header className="mb-10 max-w-2xl">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          {guestAllowed ? 'How would you like to finish?' : 'Sign in to finish booking'}
        </h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          Appointments live in your account, which is how you see, move or cancel them afterwards
          and how we reach you if anything changes.
        </p>
      </header>

      <div className="flex max-w-xl flex-col gap-4 sm:flex-row">
        <Button to={`/${slug}/signup?next=${next}`} size="lg" className="sm:flex-1">
          Create an account
        </Button>
        <Button to={`/${slug}/signin?next=${next}`} variant="outlined" size="lg" className="sm:flex-1">
          Sign in
        </Button>
      </div>

      <p className="mt-6 font-body-md text-body-md text-on-surface-variant">
        Everything you have chosen is saved — you will come straight back to this step.
      </p>

      {guestAllowed && (
        <div className="mt-10 max-w-xl border-t border-primary/10 pt-8">
          <h2 className="mb-2 font-display text-headline-sm text-on-surface">Book as a guest</h2>
          <p className="mb-6 font-body-md text-body-md text-on-surface-variant">
            No account and no password — your name and a phone number are enough, and the
            confirmation is the same. Changing or cancelling afterwards means calling the salon,
            because there is no account for the appointment to live in.
          </p>
          <Button variant="tonal" size="lg" icon="person" onClick={onGuest}>
            Continue as a guest
          </Button>
        </div>
      )}
    </div>
  )
}

/** G6 + G7 — the customer's details, the coupon, the full review, and the one submit. */
export function DetailsStep() {
  const { slug, bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const toast = useToast()
  const selection = useAppSelector((s) => s.selection)
  const { isAuthenticated, customer, loading: sessionLoading } = useSession()

  // Guest checkout is the old quick-booking path: the backend matches or creates
  // a customer from the phone number, so this needs no token. The choice belongs
  // to this visit rather than to the cart, so it stays in component state — a
  // refresh brings the three doors back, and nothing typed here was saved anyway.
  const [guest, setGuest] = useState(false)
  const guestAllowed = bootstrap.flags.guest_checkout

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [prefilled, setPrefilled] = useState(false)
  const [note, setNote] = useState('')
  const [code, setCode] = useState(selection.couponCode ?? '')
  const [couponMessage, setCouponMessage] = useState<string | null>(null)
  const [couponInvalid, setCouponInvalid] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState<unknown>(null)

  const [validateCoupon, couponState] = useValidateCouponMutation()
  const [createBooking, bookingState] = useCreateBookingMutation()

  const subtotal = selectSubtotal(selection)
  const total = selectTotal(selection)
  const duration = selectTotalDuration(selection)
  const branch = bootstrap.branches.find((b) => b.id === selection.branchId)
  const startsAt = selection.slotStart
  const endsAt = startsAt ? addMinutesToIso(startsAt, duration) : null

  const serviceIds = useMemo(() => selection.services.map((s) => s.id), [selection.services])

  // A signed-in customer already told us all of this once. The profile arrives a
  // moment after the step mounts, so fill in on arrival rather than from initial
  // state — and only once, or every keystroke would be overwritten. Anything the
  // customer has already typed wins: these fields stay editable, because the
  // person booking is not always the person who owns the account.
  useEffect(() => {
    if (prefilled || !customer) return
    setName((current) => current || customer.name || '')
    setPhone((current) => current || toNationalDigits(customer.phone ?? ''))
    setEmail((current) => current || customer.email || '')
    setPrefilled(true)
  }, [customer, prefilled])

  const applyCoupon = async () => {
    const trimmed = code.trim()
    if (!trimmed || selection.branchId === null) return
    setCouponMessage(null)
    try {
      const result = await validateCoupon({
        slug,
        code: trimmed,
        branchId: selection.branchId,
        serviceIds,
        subtotal,
      }).unwrap()

      if (result.valid) {
        dispatch(setCoupon({ code: result.code, discount: result.discount }))
        setCouponInvalid(false)
        setCouponMessage(
          result.message ??
            `Promo code ${result.code} applied — ${formatPrice(result.discount, bootstrap.currency)} off.`,
        )
      } else {
        dispatch(setCoupon(null))
        setCouponInvalid(true)
        setCouponMessage(result.message ?? 'That promo code is not valid or has expired.')
      }
    } catch {
      dispatch(setCoupon(null))
      setCouponInvalid(true)
      setCouponMessage('We could not check that code just now. Try again.')
    }
  }

  const clearCoupon = () => {
    dispatch(setCoupon(null))
    setCode('')
    setCouponInvalid(false)
    setCouponMessage(null)
  }

  const submit = async () => {
    const found = validate(name, phone, email)
    setErrors(found)
    setSubmitError(null)
    if (Object.keys(found).length > 0 || !startsAt || selection.branchId === null) {
      if (Object.keys(found).length > 0) toast.error('Check your contact details and try again.')
      return
    }

    // One key per attempt: a retry after a network wobble reuses it, so the
    // backend can recognise the second request as the same booking.
    const key = selection.idempotencyKey ?? newIdempotencyKey()
    if (selection.idempotencyKey !== key) dispatch(setIdempotencyKey(key))

    try {
      const booking = await createBooking({
        slug,
        idempotencyKey: key,
        branchId: selection.branchId,
        startsAt,
        services: selection.services.map((s) => ({
          service_id: s.id,
          staff_id: s.staffId,
          is_favorite_staff: s.staffId !== null && s.favourite,
        })),
        customer: { name: name.trim(), phone: toE164(phone), email: email.trim() || null },
        note: note.trim() || null,
        couponCode: selection.couponCode,
        sameStaff: selection.sameStaff && selection.services.every((s) => s.staffId === null),
      }).unwrap()

      rememberBooking(slug, booking)
      // The selection is deliberately left alone here. Clearing it re-renders the
      // step guard this component is still mounted inside, and its redirect to
      // step 1 wins the race against this navigate — which is exactly how the
      // confirmation screen came to be skipped (TC-BOOK-002). DoneStep resets the
      // cart in an effect once it has mounted, where nothing can race it.
      // `ref` keeps the screen rebuildable after a refresh, when the router state
      // is gone but the booking is still in sessionStorage.
      navigate(`../done?ref=${encodeURIComponent(booking.code)}`, { replace: true, state: { booking } })
    } catch (error) {
      // The key stays in the slice on purpose: the next tap is the same attempt.
      setSubmitError(error)
    }
  }

  useWizardAction(
    isAuthenticated || guest
      ? {
          label: 'Confirm booking',
          onClick: submit,
          loading: bookingState.isLoading,
          disabled: bookingState.isLoading,
        }
      : null,
    [name, phone, email, note, selection, bookingState.isLoading, isAuthenticated, guest],
  )

  // An account is still the better ending — it is the only way the customer can
  // come back to see, move or cancel the appointment — so someone who got this
  // far without one is asked first. The cart survives the round trip to sign-in
  // in sessionStorage, so they land back on this step with everything chosen.
  if (!isAuthenticated && !guest) {
    return (
      <FinishOptions
        slug={slug}
        checking={sessionLoading}
        guestAllowed={guestAllowed}
        onGuest={() => setGuest(true)}
      />
    )
  }

  return (
    <div className="container-page py-12 md:py-section-gap-md">
      <header className="mb-12 max-w-2xl">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          {isAuthenticated ? 'Your details' : 'Guest information'}
        </h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          {isAuthenticated ? (
            <>
              These come from your account. Change anything that should be different for this visit —
              we use them to send your confirmation and any updates about your appointment.
            </>
          ) : (
            <>
              No account needed — we use these to send your confirmation and any updates about your
              appointment. Already have an account?{' '}
              <Link
                to={`/${slug}/signin?next=${encodeURIComponent(`/${slug}/book/details`)}`}
                className="text-primary underline"
              >
                Sign in
              </Link>
            </>
          )}
        </p>
      </header>

      {/* Stitch runs the form across seven of twelve columns and floats the
          summary alongside it, so the fields keep a comfortable measure. */}
      <div className="grid grid-cols-1 gap-gutter md:grid-cols-12">
        <form
          className="flex flex-col gap-8 md:col-span-7 md:pr-12"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <Input
            label="Full name"
            required
            autoComplete="name"
            value={name}
            error={errors.name}
            placeholder="Alex Morgan"
            onChange={(e) => setName(e.target.value)}
          />
          <PhoneInput
            label="Phone number"
            required
            value={phone}
            error={errors.phone}
            hint="We'll call or text this number to confirm."
            onChange={setPhone}
          />
          <Input
            label="Email address (optional)"
            type="email"
            autoComplete="email"
            value={email}
            error={errors.email}
            hint="Add one if you'd like a confirmation email too."
            onChange={(e) => setEmail(e.target.value)}
          />
          <Textarea
            label="Notes for the salon (optional)"
            value={note}
            rows={3}
            placeholder="Anything we should know before your visit"
            onChange={(e) => setNote(e.target.value)}
          />

          <section className="rounded-xl border border-primary/10 bg-surface-container-low p-8">
            <h2 className="mb-6 font-label-caps text-label-caps uppercase text-on-surface-variant">Promo code</h2>
            {selection.couponCode ? (
              <div className="flex items-center justify-between gap-4">
                <p className="font-body-md text-body-md text-on-surface">
                  <span className="material-symbols-outlined mr-2 align-middle text-[18px] text-primary">
                    check_circle
                  </span>
                  {selection.couponCode} — {formatPrice(selection.couponDiscount, bootstrap.currency)} off
                </p>
                <Button variant="text" size="sm" onClick={clearCoupon}>
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex items-end gap-3">
                <Input
                  label="Enter your code"
                  containerClassName="flex-1"
                  value={code}
                  autoCapitalize="characters"
                  placeholder="e.g. WELCOME10"
                  onChange={(e) => {
                    setCode(e.target.value)
                    setCouponMessage(null)
                    setCouponInvalid(false)
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => void applyCoupon()}
                  loading={couponState.isLoading}
                  disabled={!code.trim()}
                  className="mb-[2px]"
                >
                  Apply
                </Button>
              </div>
            )}
            {couponMessage && (
              <p
                role="status"
                className={cn(
                  'mt-4 font-body-md text-sm',
                  couponInvalid ? 'text-error' : 'text-on-surface-variant',
                )}
              >
                {couponMessage}
              </p>
            )}
          </section>

          {submitError ? <InlineError error={submitError} onRetry={() => void submit()} /> : null}

          {/* The real submit lives in the summary bar; this keeps Enter working. */}
          <button type="submit" className="sr-only">
            Confirm booking
          </button>
        </form>

        <aside className="md:col-span-5">
          <div className="rounded-xl border border-primary/10 bg-surface-container-low p-8 md:sticky md:top-40">
            <h2 className="mb-8 border-b border-primary/10 pb-4 font-display text-headline-sm text-on-surface">
              Appointment summary
            </h2>

            <dl className="flex flex-col gap-6">
              <Row icon="location_on" label="Location">
                <p className="font-body-md text-body-md text-on-surface">{branch?.name ?? '—'}</p>
                {branch?.address && (
                  <p className="font-body-md text-body-md text-on-surface-variant">{branch.address}</p>
                )}
              </Row>

              <Row icon="schedule" label="Time">
                <p className="font-body-md text-body-md text-on-surface">
                  {startsAt ? formatLongDate(slotDate(startsAt)) : '—'}
                </p>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  {startsAt && endsAt ? `${formatSlotTime(startsAt)} – ${formatSlotTime(endsAt)}` : ''} ·{' '}
                  {formatDuration(duration)}
                </p>
              </Row>

              <Row icon="spa" label="Services">
                <ul className="flex flex-col gap-3">
                  {selection.services.map((service) => (
                    <li key={service.id} className="flex justify-between gap-4">
                      <span className="font-body-md text-body-md text-on-surface">
                        {service.name}
                        <span className="block font-body-md text-body-md text-on-surface-variant">
                          {formatDuration(service.duration)} · {service.staffName ?? 'Any specialist'}
                          {/* The heart from step 3, so the customer can see the
                              request survived all the way to the last screen. */}
                          {service.staffId !== null && service.favourite && (
                            <span
                              title="The specialist you came for"
                              className="material-symbols-outlined filled ml-1 align-middle text-[14px] text-primary"
                            >
                              favorite
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="shrink-0 font-body-md text-body-md text-on-surface">
                        {formatPrice(service.price, bootstrap.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Row>
            </dl>

            <div className="mt-8 flex flex-col gap-3 border-t border-primary/10 pt-8">
              <div className="flex justify-between font-body-md text-body-md text-on-surface-variant">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal, bootstrap.currency)}</span>
              </div>
              {selection.couponDiscount > 0 && (
                <div className="flex justify-between font-body-md text-body-md text-primary">
                  <span>Discount</span>
                  <span>-{formatPrice(selection.couponDiscount, bootstrap.currency)}</span>
                </div>
              )}
              <div className="flex items-center justify-between font-display text-headline-sm text-on-surface">
                <span>Total</span>
                <span className="text-primary">{formatPrice(total, bootstrap.currency)}</span>
              </div>
            </div>

            {bootstrap.vendor.cancellation_policy && (
              <p className="mt-8 font-body-md text-sm text-on-surface-variant">
                {bootstrap.vendor.cancellation_policy}
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}

function Row({ icon, label, children }: { icon: string; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <span className="material-symbols-outlined mt-1 text-[20px] text-outline">{icon}</span>
      <div className="min-w-0 flex-1">
        <dt className="mb-1 font-label-caps text-label-caps uppercase text-outline">{label}</dt>
        <dd>{children}</dd>
      </div>
    </div>
  )
}
